/**
 * Toast context — provider, state, auto-dismiss timers, and the `useToast` hook
 * (E10-F02-T03).
 *
 * Parity with the legacy `core.showNotification`: a single bottom-right stack,
 * each toast auto-dismisses after ~3000ms with a 300ms reverse animation before
 * it leaves the DOM. The React port adds a typed API and a real stack (the
 * legacy code already supported multiple concurrent toasts via the shared
 * container — this formalises it).
 *
 * Public API ({@link ToastApi} via {@link useToast}):
 *   - `toast(message, type?, options?)` → raise any toast, returns its id;
 *   - `success(message, options?)` / `error(message, options?)` → conveniences;
 *   - `dismiss(id)` → start the exit animation + removal early.
 *
 * De-duplication (E02-F03-T10): raising a toast whose type + message equal a toast
 * still on screen returns that toast's id (and restarts its auto-dismiss) instead
 * of stacking an identical copy.
 *
 * Screen-change sweep (E06-F01-T15): {@link useClearToastsOnScreenChange} dismisses
 * toasts left over from the previous screen whenever its `screenKey` changes —
 * except those raised within {@link TOAST_SCREEN_GRACE_MS} (the toast of the very
 * action that navigated survives the switch).
 *
 * Outside a provider the API is a safe no-op (returns an empty id, dismiss does
 * nothing) so a stray `useToast()` never throws — mirrors the legacy guard
 * `if (!container) return;` when `#toast-container` was absent from the DOM.
 *
 * The {@link ToastProvider} renders its children AND the {@link ToastContainer}
 * (the React equivalent of the static `#toast-container`), so mounting the
 * provider once near the app root is all that is needed.
 */
import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
  type ReactNode,
} from 'react';
import { ToastContainer } from './Toast.js';
import {
  makeToast,
  TOAST_EXIT_MS,
  TOAST_SCREEN_GRACE_MS,
  type Toast,
  type ToastOptions,
  type ToastType,
} from './toast-model.js';

export interface ToastApi {
  /** Raise a toast of any type. Returns the new toast id. */
  readonly toast: (message: string, type?: ToastType, options?: ToastOptions) => string;
  /** Raise a success (green) toast. Returns the new toast id. */
  readonly success: (message: string, options?: ToastOptions) => string;
  /** Raise an error (red) toast. Returns the new toast id. */
  readonly error: (message: string, options?: ToastOptions) => string;
  /** Dismiss a toast by id (plays the exit animation, then removes it). */
  readonly dismiss: (id: string) => void;
}

/** Default API outside a provider: no container in the DOM → no-ops. */
const NOOP_API: ToastApi = {
  toast: () => '',
  success: () => '',
  error: () => '',
  dismiss: () => {},
};

const ToastContext = createContext<ToastApi>(NOOP_API);

/**
 * E06-F01-T15 — internal: dismiss every live toast raised more than
 * {@link TOAST_SCREEN_GRACE_MS} ago. Kept off {@link ToastApi} (a separate
 * context) so the public toast API contract does not change.
 */
const ToastSweepContext = createContext<() => void>(() => {});

export interface ToastProviderProps {
  readonly children: ReactNode;
}

export function ToastProvider({ children }: ToastProviderProps): React.JSX.Element {
  const [toasts, setToasts] = useState<Toast[]>([]);
  // Ids currently playing their exit animation (drives the reverse transition
  // before the node is unmounted).
  const [leaving, setLeaving] = useState<ReadonlySet<string>>(() => new Set());

  // All pending timers (auto-dismiss + exit-removal), keyed for cleanup so an
  // unmount or an early manual dismiss never leaves a dangling timeout that
  // would call setState on an unmounted tree.
  const timers = useRef<Map<string, ReturnType<typeof setTimeout>>>(new Map());

  // E02-F03-T10 — de-duplication index: `type + message` → id of the LIVE (not yet
  // leaving) toast showing it. Raising the same text again while it is still on
  // screen re-uses that toast instead of stacking a copy (Ксения: three clicks on
  // a failing Save = three identical sticky «Could not save the project» toasts).
  const liveByKey = useRef<Map<string, string>>(new Map());

  // E06-F01-T15 — when each live toast was (last) raised; drives the
  // screen-change sweep's grace window.
  const raisedAt = useRef<Map<string, number>>(new Map());

  const forgetLive = useCallback((id: string): void => {
    for (const [key, liveId] of liveByKey.current) {
      if (liveId === id) liveByKey.current.delete(key);
    }
    raisedAt.current.delete(id);
  }, []);

  const clearTimer = useCallback((key: string): void => {
    const t = timers.current.get(key);
    if (t !== undefined) {
      clearTimeout(t);
      timers.current.delete(key);
    }
  }, []);

  // Remove a toast from the DOM. Internal — called after the exit animation.
  const remove = useCallback(
    (id: string): void => {
      clearTimer(`dismiss:${id}`);
      clearTimer(`exit:${id}`);
      forgetLive(id);
      setToasts((prev) => prev.filter((t) => t.id !== id));
      setLeaving((prev) => {
        if (!prev.has(id)) return prev;
        const next = new Set(prev);
        next.delete(id);
        return next;
      });
    },
    [clearTimer, forgetLive],
  );

  // Start the exit animation for a toast, then remove it after TOAST_EXIT_MS.
  // Idempotent: a toast already leaving is not re-armed.
  const dismiss = useCallback(
    (id: string): void => {
      // Cancel a pending auto-dismiss — we are leaving now.
      clearTimer(`dismiss:${id}`);
      // A leaving toast no longer absorbs repeats: the same text raised during
      // the exit animation shows up as a fresh toast.
      forgetLive(id);
      setLeaving((prev) => {
        if (prev.has(id)) return prev;
        const next = new Set(prev);
        next.add(id);
        return next;
      });
      // Arm the unmount once (guard against double-dismiss re-arming).
      if (!timers.current.has(`exit:${id}`)) {
        timers.current.set(
          `exit:${id}`,
          setTimeout(() => remove(id), TOAST_EXIT_MS),
        );
      }
    },
    [clearTimer, remove, forgetLive],
  );

  const raise = useCallback(
    (message: string, type: ToastType = 'success', options: ToastOptions = {}): string => {
      const next = makeToast(message, type, options);
      const key = `${type}\u0000${message}`;
      const existing = liveByKey.current.get(key);
      if (existing !== undefined) {
        // Same text already on screen → keep ONE toast; restart its auto-dismiss
        // clock (a sticky repeat stays sticky) and hand back the existing id.
        clearTimer(`dismiss:${existing}`);
        raisedAt.current.set(existing, Date.now());
        if (next.duration > 0) {
          timers.current.set(
            `dismiss:${existing}`,
            setTimeout(() => dismiss(existing), next.duration),
          );
        }
        return existing;
      }
      liveByKey.current.set(key, next.id);
      raisedAt.current.set(next.id, Date.now());
      setToasts((prev) => [...prev, next]);
      // Arm auto-dismiss unless the toast is sticky (duration <= 0).
      if (next.duration > 0) {
        timers.current.set(
          `dismiss:${next.id}`,
          setTimeout(() => dismiss(next.id), next.duration),
        );
      }
      return next.id;
    },
    [dismiss, clearTimer],
  );

  // E06-F01-T15 — screen changed: drop toasts that belong to the previous screen.
  const sweepStale = useCallback((): void => {
    const cutoff = Date.now() - TOAST_SCREEN_GRACE_MS;
    for (const [id, at] of [...raisedAt.current]) {
      if (at < cutoff) dismiss(id);
    }
  }, [dismiss]);

  // Clear every outstanding timer on unmount.
  useEffect(() => {
    const map = timers.current;
    return () => {
      for (const t of map.values()) clearTimeout(t);
      map.clear();
    };
  }, []);

  const api = useMemo<ToastApi>(
    () => ({
      toast: raise,
      success: (message, options) => raise(message, 'success', options),
      error: (message, options) => raise(message, 'error', options),
      dismiss,
    }),
    [raise, dismiss],
  );

  return (
    <ToastContext.Provider value={api}>
      <ToastSweepContext.Provider value={sweepStale}>
        {children}
        <ToastContainer toasts={toasts} leaving={leaving} onDismiss={dismiss} />
      </ToastSweepContext.Provider>
    </ToastContext.Provider>
  );
}

/**
 * Access the toast API. Outside a {@link ToastProvider} this returns the no-op
 * API (safe to call from anywhere, never throws).
 */
export function useToast(): ToastApi {
  return useContext(ToastContext);
}

/**
 * E06-F01-T15 — dismiss toasts left over from the previous screen whenever
 * `screenKey` changes (not on mount). A toast raised within
 * {@link TOAST_SCREEN_GRACE_MS} before the change survives — it is the feedback
 * of the action that navigated. Mount once per screen-switching host (the app
 * router, the Research section's internal screen machine). No-op outside a
 * {@link ToastProvider}.
 */
export function useClearToastsOnScreenChange(screenKey: string): void {
  const sweep = useContext(ToastSweepContext);
  const previous = useRef(screenKey);
  useEffect(() => {
    if (previous.current === screenKey) return;
    previous.current = screenKey;
    sweep();
  }, [screenKey, sweep]);
}
